import { Component, inject, computed, signal, OnDestroy, OnInit, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './services/auth.service';
import { ThemeService } from './services/theme.service';
import { LoginBrandingService } from './services/login-branding.service';
import { Router } from '@angular/router';
import { EntraBranding, EntraBrandingService } from './services/entra-branding.service';
import { LoginComponent } from './components/login/login.component';
import { ComingSoonComponent } from './components/coming-soon/coming-soon.component';
import { AnamAgentComponent } from './components/anam/anam-agent.component';
import { CustomFoundryChatComponent } from './components/custom-foundry-chat/custom-foundry-chat.component';
import { MsalService } from '@azure/msal-angular';
import { environment } from './environments/environment';
import { getMsalScopesForActiveClientId, getSilentRedirectUri } from './utils/msal-config';
import { firstValueFrom } from 'rxjs';

export interface NavItem {
  id: string;
  licenseMatchId?: string;
  licenseMatchAliases?: string[];
  label: string;
  category: string;
  type: 'agent' | 'tool' | 'anam' | 'custom';
  dotColor?: string;
  sidebarBadgeText?: string;
  sidebarIconUrl?: string;
  sidebarIconAlt?: string;
  iconPath?: string;
  chatConfig: { title: string; secret: string };
  isComingSoon?: boolean;
}

interface AuthClientSettings {
  InactivityLogoutMinutes: number;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, LoginComponent, ComingSoonComponent, AnamAgentComponent, CustomFoundryChatComponent],
  templateUrl: './app.component.html',
  host: {
    // Escape cierra el menú de usuario, igual que un clic fuera.
    '(document:keydown.escape)': 'closeUserMenu()',
  },
})
export class AppComponent implements OnInit, OnDestroy {
  public readonly authService = inject(AuthService);
  /**
   * Se instancia aquí, al arrancar, y no cuando el login se pinta: así ya sabe si la URL trae una
   * clave de cliente y el primer cuadro es el loader, no la pantalla por defecto.
   */
  private readonly loginBranding = inject(LoginBrandingService);

  /** URL del logo que falló al pintarse en el HOME; se vuelve al logo M31 en vez de dejar un icono roto. */
  private readonly logoClienteFallido = signal<string | null>(null);

  /** Logo del cliente para el HOME, o null si no entró por una ruta de cliente o no se pudo cargar. */
  readonly logoHome = computed(() => {
    const url = this.loginBranding.logoCliente();
    return url && url !== this.logoClienteFallido() ? url : null;
  });

  logoClienteFalla(url: string): void {
    this.logoClienteFallido.set(url);
  }
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  private readonly msalService = inject(MsalService, { optional: true });
  private readonly entraBrandingService = inject(EntraBrandingService);
  private readonly http = inject(HttpClient);
  private readonly sidebarConfig = environment.sidebar;
  private readonly activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart', 'focus'];

  readonly isLoggedIn = this.authService.isLoggedIn;
  readonly isDarkMode = this.themeService.isDarkMode;

  /**
   * Arranca minimizada: al entrar, la pantalla de bienvenida ya ofrece los agentes, así que la
   * barra solo estorbaría. El usuario la despliega con el botón de menú cuando la necesita.
   */
  /** Minutos antes de la caducidad en los que se renueva el token de Microsoft. */
  private static readonly MargenRenovacionMinutos = 5;

  private microsoftTokenWatchId: number | null = null;

  readonly isSidebarExpanded = signal<boolean>(false);
  readonly isUserMenuOpen = signal<boolean>(false);
  /** Interruptor de la función, configurable en environment.ts sin tocar el backend. */
  readonly consumoPropioActivo = environment.features.personalUsage;

  readonly consumoPropio = signal<{ inputTokens: number; outputTokens: number; messages: number } | null>(null);
  readonly consumoPropioCargando = signal<boolean>(false);
  readonly consumoPropioError = signal<boolean>(false);
  readonly isChatResetting = signal<boolean>(false);
  readonly entraBranding = signal<EntraBranding | null>(null);

  /**
   * El menú se arma en ngOnInit, después del primer pintado. Con la aplicación sin zone.js, un
   * campo normal no avisa de ese cambio y Angular acababa lanzando NG0100: la fila del agente se
   * creaba una vez que su padre ya se había comprobado. Como señal, el cambio se anuncia y la
   * plantilla se vuelve a evaluar de forma ordenada.
   *
   * Se conservan el getter y el setter para que el resto del componente siga escribiendo
   * `this.navItems = [...]` como hasta ahora.
   */
  private readonly navItemsSignal = signal<NavItem[]>([]);

  get navItems(): NavItem[] {
    return this.navItemsSignal();
  }

  set navItems(valor: NavItem[]) {
    this.navItemsSignal.set(valor);
  }
  readonly activeNavItem = signal<NavItem | null>(null);
  private inactivityLogoutMinutes = 15;
  private inactivityTimerId: number | null = null;
  private sessionExpirationTimerId: number | null = null;
  private activityListenersAttached = false;
  private lastSessionRefreshAt = 0;
  private readonly handleUserActivity = () => this.scheduleInactivityLogout();

  constructor() {
    effect(() => {
      if (!this.canAccessBots()) {
        this.activeNavItem.set(null);
      } else {
        const current = this.activeNavItem();
        const disponibles = this.getVisibleNavItems();
        const currentStillAvailable = Boolean(current && disponibles.some(item => item.id === current.id));

        if (!currentStillAvailable) {
          this.aplicarAgentePreferido();
        }
      }

      if (this.isLoggedIn()) {
        this.startInactivityWatch();
        this.startSessionExpirationWatch();
      } else {
        this.stopInactivityWatch();
      }
    });
  }

  readonly userProfile = computed(() => {
    const user = this.authService.currentUser();
    if (!user) return { name: 'Invitado', level: 'Acceso Limitado', companyName: '', initials: 'IN', photoUrl: undefined };
    return {
      name: user.name,
      level: `Usuario ${this.planTierLabel()}`,
      companyName: user.companyName?.trim() || '',
      initials: user.name.substring(0, 2).toUpperCase(),
      photoUrl: user.photoUrl
    };
  });

  readonly planTierLabel = computed(() => {
    const count = this.agents.length;

    if (count === 1) {
      return 'LITE';
    }

    if (count > 4) {
      return 'ELITE';
    }

    return 'ELITE';
  });

  /**
   * Saludo según la hora local. Se calcula una sola vez al entrar: cambiarlo en vivo no aporta
   * nada y obligaría a un temporizador.
   */
  readonly welcomeGreeting: string = (() => {
    const hora = new Date().getHours();
    if (hora < 12) { return 'Buenos días'; }
    if (hora < 19) { return 'Buenas tardes'; }
    return 'Buenas noches';
  })();

  /** El SIIC devuelve el nombre en minúsculas; en el saludo se muestra capitalizado. */
  readonly welcomeName = computed<string>(() => {
    const nombre = this.userProfile().name?.trim() ?? '';
    return nombre ? nombre.charAt(0).toUpperCase() + nombre.slice(1) : '';
  });

  get agents() {
    return this.navItems.filter(item => {
      if (item.category !== 'AGENTES ACTIVOS') return false;
      return this.matchesAnyLicenseKey(item);
    });
  }

  get betas() {
    if (!this.sidebarConfig.showBetaSection) {
      return [];
    }

    return this.navItems.filter(i => i.category === 'BETA' && (i.id !== 'm31' || this.sidebarConfig.showM31IaItem));
  }

  get tools() {
    if (!this.sidebarConfig.showAzureCreativeSection) {
      return [];
    }

    return this.navItems.filter(i => {
      if (i.category !== 'AZURE CREATIVE') {
        return false;
      }

      if (i.id === 'images') {
        return this.sidebarConfig.showCreateImagesItem;
      }

      if (i.id === 'video') {
        return this.sidebarConfig.showCreateVideosItem;
      }

      if (i.id === 'audio') {
        return this.sidebarConfig.showCreateAudioItem;
      }

      return true;
    });
  }

  get avatares() {
    if (!this.sidebarConfig.showAvatarIaSection) {
      return [];
    }

    const isSsoUser = this.authService.requiresMicrosoftLoginForCurrentUser();

    return this.navItems.filter(i => {
      if (i.category !== 'AVATAR IA') {
        return false;
      }

      if (i.id === 'anam-avatar' && !this.sidebarConfig.showAvatarItem) {
        return false;
      }

      if (isSsoUser) {
        return true;
      }

      return this.authService.tieneLicencia(i.licenseMatchId ?? i.id);
    });
  }

  /**
   * Es computed y no un método porque la aplicación corre sin zone.js: la plantilla decide con
   * esto si muestra el login o el espacio de trabajo, y necesita depender de un nodo reactivo.
   * Como método, el cambio de sesión no siempre repintaba la vista y el usuario se quedaba en
   * la pantalla de login hasta recargar.
   */
  readonly canAccessBots = computed<boolean>(() => {
    if (!this.authService.isLoggedIn()) {
      return false;
    }

    if (!this.authService.requiresMicrosoftLogin()) {
      return true;
    }

    return Boolean(this.authService.msalAccessToken());
  });

  async ngOnInit(): Promise<void> {

    await this.loadAuthClientSettings();
    this.initializeNavItems();
    if (this.canAccessBots()) {
      this.aplicarAgentePreferido();
    }

    if (!this.msalService) {
      return;
    }

    try {


      const res = await this.msalService.instance.handleRedirectPromise();

      if (res) {
        console.log('[MSAL] Redirect procesado en AppComponent:', res.account?.username);
        this.msalService.instance.setActiveAccount(res.account);
        this.authService.setMicrosoftToken(res.accessToken);
        await this.loadEntraBranding();
        this.aplicarAgentePreferido();
        return;
      }

      // Si el usuario requiere Microsoft, se restaura el token en silencio. Un token guardado solo
      // sirve si de verdad le queda vida: antes se daba por bueno aunque llevara días caducado, y
      // el chat quedaba inservible sin que la aplicación se enterara.
      const minutosRestantes = this.authService.minutosRestantesTokenMicrosoft();
      const tokenUtilizable = Boolean(this.authService.msalAccessToken())
        && (minutosRestantes === null || minutosRestantes > AppComponent.MargenRenovacionMinutos);

      if (!this.authService.requiresMicrosoftLoginForCurrentUser() || tokenUtilizable) {
        if (this.authService.msalAccessToken()) {
          await this.loadEntraBranding();
        }
        this.startMicrosoftTokenWatch();
        return;
      }

      const activeAccount = this.msalService.instance.getActiveAccount();
      const fallbackAccount = this.msalService.instance.getAllAccounts()[0] ?? null;
      const account = activeAccount ?? fallbackAccount;
      if (!account) {
        return;
      }

      this.msalService.instance.setActiveAccount(account);
      const scopes = getMsalScopesForActiveClientId();
      const silent = await this.msalService.instance.acquireTokenSilent({
        scopes,
        account,
        redirectUri: getSilentRedirectUri()
      });

      if (silent?.accessToken) {
        this.authService.setMicrosoftToken(silent.accessToken);
        await this.loadEntraBranding();
        this.aplicarAgentePreferido();
      }

      this.startMicrosoftTokenWatch();
    } catch (err) {
      console.error('[MSAL] Error en handleRedirectPromise de AppComponent:', err);

      // No se pudo renovar y el token guardado ya no sirve: se le ofrece reconectar en vez de
      // dejarlo con los agentes en "No disponible".
      const minutos = this.authService.minutosRestantesTokenMicrosoft();
      if (this.authService.requiresMicrosoftLoginForCurrentUser()
        && (minutos === null || minutos <= AppComponent.MargenRenovacionMinutos)) {
        this.authService.marcarSesionMicrosoftCaducada();
      }

      this.startMicrosoftTokenWatch();
    }
  }

  /**
   * Renueva el token de Microsoft antes de que caduque, mientras el usuario trabaja. Así la
   * mayoría no llega a ver el aviso de reconexión: solo lo verán quienes de verdad ya no puedan
   * renovar en silencio, por ejemplo tras dejar el equipo suspendido toda la noche.
   */
  private startMicrosoftTokenWatch(): void {
    if (this.microsoftTokenWatchId !== null || !this.msalService) {
      return;
    }

    this.microsoftTokenWatchId = setInterval(() => {
      void this.renovarTokenMicrosoftSiHaceFalta();
    }, 60_000) as unknown as number;
  }

  private stopMicrosoftTokenWatch(): void {
    if (this.microsoftTokenWatchId !== null) {
      clearInterval(this.microsoftTokenWatchId);
      this.microsoftTokenWatchId = null;
    }
  }

  private async renovarTokenMicrosoftSiHaceFalta(): Promise<void> {
    if (!this.msalService
      || !this.authService.isLoggedIn()
      || !this.authService.requiresMicrosoftLoginForCurrentUser()
      || this.authService.microsoftSessionExpired()) {
      return;
    }

    const minutos = this.authService.minutosRestantesTokenMicrosoft();
    if (minutos !== null && minutos > AppComponent.MargenRenovacionMinutos) {
      return;
    }

    const cuenta = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0]
      ?? null;

    if (!cuenta) {
      this.authService.marcarSesionMicrosoftCaducada();
      return;
    }

    try {
      const renovado = await this.msalService.instance.acquireTokenSilent({
        scopes: getMsalScopesForActiveClientId(),
        account: cuenta,
        redirectUri: getSilentRedirectUri()
      });

      if (renovado?.accessToken) {
        this.authService.setMicrosoftToken(renovado.accessToken);
        return;
      }

      this.authService.marcarSesionMicrosoftCaducada();
    } catch (error) {
      console.warn('[MSAL] No se pudo renovar el token en segundo plano; se pedirá reconectar.', error);
      this.authService.marcarSesionMicrosoftCaducada();
    }
  }

  ngOnDestroy(): void {
    this.stopInactivityWatch();
    this.stopMicrosoftTokenWatch();
    this.entraBrandingService.clear();
  }

  /**
   * Ya no se abre un agente automáticamente: al entrar se muestra la pantalla de bienvenida y el
   * chat aparece solo cuando el usuario elige un agente del menú. Únicamente se preselecciona si
   * environment.auth.preferredAgentId está configurado.
   */
  private aplicarAgentePreferido(): void {
    const preferredAgentId = environment.auth.preferredAgentId?.trim().toUpperCase();
    const preferredAgent = preferredAgentId
      ? this.agents.find(item => item.id.toUpperCase() === preferredAgentId)
      : null;

    this.activeNavItem.set(null);

    if (preferredAgent) {
      this.selectNavItem(preferredAgent);
    }
  }

  private getVisibleNavItems(): NavItem[] {
    return [...this.agents, ...this.avatares, ...this.betas, ...this.tools];
  }

  private initializeNavItems(): void {
    this.navItems = [
      {
        id: 'DELTA',
        licenseMatchAliases: ['M31 - DELTA (DYNAMICS 365 BUSINESS CENTRAL)', 'DYNAMICS', 'DYNAMICS BC 365'],
        label: 'Delta (Dynamics BC 365)',
        category: 'AGENTES ACTIVOS',
        type: 'custom',
        dotColor: 'bg-blue-500',
        chatConfig: { title: 'Asistente Delta', secret: '' }
      },
      {
        id: 'ALPHERATZ',
        licenseMatchId: 'ASPEL',
        licenseMatchAliases: ['M31 - ALPHERATZ (ASPEL)', 'SIIGO ASPEL', 'ASPEL SQL'],
        label: 'Alpheratz (Siigo Aspel / SQL)',
        category: 'AGENTES ACTIVOS',
        type: 'custom',
        dotColor: 'bg-purple-500',
        chatConfig: { title: 'Asistente Alpheratz', secret: '' }
      },
      {
        id: 'MU',
        licenseMatchAliases: ['M31 - MU (SITIO WEB - ORGANIZACIÓN)', 'M31 - MU (SITIO WEB - ORGANIZACION)', 'SITIO WEB', 'MU SITIO WEB', 'MUSITIOWEB'],
        label: 'MU (Sitio Web)',
        category: 'AGENTES ACTIVOS',
        type: 'custom',
        dotColor: 'bg-pink-500',
        chatConfig: { title: 'Asistente MU', secret: '' }
      },
      {
        id: 'MIRACH',
        licenseMatchAliases: ['M31 - MIRACH (M365 / SHAREPOINT-ONEDRIVE)', 'SHAREPOINT', 'ONEDRIVE'],
        label: 'Mirach (SharePoint /OneDrive)',
        category: 'AGENTES ACTIVOS',
        type: 'custom',
        dotColor: 'bg-cyan-500',
        chatConfig: { title: 'Asistente Mirach', secret: '' }
      },
      {
        id: 'CUSTOM_AGENT',
        licenseMatchId: 'CUSTOM AGENT',
        licenseMatchAliases: ['M31 - CUSTOM AGENT'],
        label: 'Custom Agent',
        category: 'AGENTES ACTIVOS',
        type: 'custom',
        dotColor: 'bg-orange-500',
        chatConfig: { title: 'Asistente Custom Agent', secret: '' }
      },
      {
        id: 'anam-avatar',
        licenseMatchId: 'ANAM',
        label: 'Ana',
        category: 'AVATAR IA',
        type: 'anam',
        dotColor: 'bg-violet-500',
        chatConfig: { title: 'Avatar IA', secret: '' }
      },
      // {
      //   id: 'm31',
      //   label: 'M31 IA',
      //   category: 'BETA',
      //   type: 'agent',
      //   dotColor: 'bg-green-500',
      //   chatConfig: { title: 'Asistente General M31', secret: '' },
      //   isComingSoon: true
      // },
      // {
      //   id: 'images',
      //   label: 'CREAR IMÁGENES',
      //   category: 'AZURE CREATIVE',
      //   type: 'tool',
      //   iconPath: 'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z',
      //   chatConfig: { title: 'Generador de Imágenes', secret: '' },
      //   isComingSoon: true
      // },
      // {
      //   id: 'video',
      //   label: 'CREAR VIDEOS',
      //   category: 'AZURE CREATIVE',
      //   type: 'tool',
      //   iconPath: 'M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z',
      //   chatConfig: { title: 'Generador de Video', secret: '' },
      //   isComingSoon: true
      // },
      // {
      //   id: 'audio',
      //   label: 'CREAR AUDIO',
      //   category: 'AZURE CREATIVE',
      //   type: 'tool',
      //   iconPath: 'M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z',
      //   chatConfig: { title: 'Generador de Audio', secret: '' },
      //   isComingSoon: true
      // }
    ];

    this.aplicarIconosConfigurados();
  }

  /**
   * Los iconos se configuran en environment.agentIcons, no aquí. Si una URL se deja vacía o
   * se borra su línea, ese agente vuelve a mostrar su etiqueta de texto.
   */
  private aplicarIconosConfigurados(): void {
    const iconos = environment.agentIcons ?? {};

    const items = this.navItems;
    for (const item of items) {
      const url = iconos[item.id]?.trim();
      item.sidebarIconUrl = url ? url : undefined;
      item.sidebarIconAlt = url ? item.label : undefined;
    }

    // Los iconos se asignaron sobre los objetos ya existentes: hay que anunciar el cambio.
    this.navItemsSignal.set([...items]);
  }

  selectNavItem(selectedItem: NavItem): void {
    if (this.activeNavItem() === selectedItem) return;
    this.isChatResetting.set(true);

    // El secreto ya no se lleva al navegador: el chat de Foundry se autentica en el backend.
    selectedItem.chatConfig = {
      ...selectedItem.chatConfig,
      secret: ''
    };

    this.activeNavItem.set(selectedItem);
    setTimeout(() => this.isChatResetting.set(false), 10);
  }

  private matchesAnyLicenseKey(item: NavItem): boolean {
    const keys = this.getLicenseKeysForItem(item);
    return keys.some((key) => this.authService.tieneLicencia(key));
  }

  private getLicenseKeysForItem(item: NavItem): string[] {
    const baseKey = item.licenseMatchId ?? item.id;
    const aliases = item.licenseMatchAliases ?? [];
    return [baseKey, ...aliases].filter((value) => value.trim().length > 0);
  }

  toggleSidebar(): void { this.isSidebarExpanded.update(v => !v); }
  toggleTheme(): void { this.themeService.toggleTheme(); }
  toggleUserMenu(): void {
    const abierto = !this.isUserMenuOpen();
    this.isUserMenuOpen.set(abierto);

    if (abierto && this.consumoPropioActivo) {
      void this.cargarConsumoPropio();
    }
  }

  closeUserMenu(): void { this.isUserMenuOpen.set(false); }

  /**
   * Consumo de tokens del propio usuario. Se pide cada vez que se abre el menú porque cambia
   * con cada mensaje; el panel de "Consumo" del chat es distinto: ése es de todo el dominio.
   */
  private async cargarConsumoPropio(): Promise<void> {
    this.consumoPropioCargando.set(true);
    this.consumoPropioError.set(false);

    try {
      const respuesta = await firstValueFrom(
        this.http.get<Record<string, unknown>>(`${environment.apiUrl}/foundry/usage-me`, {
          headers: this.construirCabecerasBackend()
        })
      );

      const leer = (nombre: string): number => {
        const valor = respuesta?.[nombre] ?? respuesta?.[nombre.charAt(0).toLowerCase() + nombre.slice(1)];
        return Number(valor) || 0;
      };

      this.consumoPropio.set({
        inputTokens: leer('InputTokens'),
        outputTokens: leer('OutputTokens'),
        messages: leer('Messages')
      });
    } catch {
      this.consumoPropio.set(null);
      this.consumoPropioError.set(true);
    } finally {
      this.consumoPropioCargando.set(false);
    }
  }

  private construirCabecerasBackend(): Record<string, string> {
    const cabeceras: Record<string, string> = {};

    const token = this.authService.msalAccessToken();
    if (token) { cabeceras['Authorization'] = `Bearer ${token}`; }

    const email = this.authService.currentUser()?.email?.trim();
    if (email) { cabeceras['X-M31-User-Email'] = email; }

    const ticket = this.authService.licenseSessionTicket()?.trim();
    if (ticket) { cabeceras['X-M31-License-Ticket'] = ticket; }

    return cabeceras;
  }

  /** Miles con separador; los conteos de tokens crecen rápido y sin formato no se leen. */
  formatearTokens(valor: number): string {
    return new Intl.NumberFormat('es-MX').format(Math.max(0, Math.round(valor)));
  }

  /**
   * Quien entró por m31.ionet.mx/{clave} vuelve a esa misma entrada al cerrar sesión, con su
   * personalización. Si entró por la raíz, no hay clave guardada y se queda el login de siempre.
   */
  private async volverAlAccesoDelCliente(): Promise<void> {
    const clave = await this.loginBranding.restaurarTrasCerrarSesion();
    if (clave) {
      void this.router.navigateByUrl(`/${clave}`);
    }
  }

  logout(): void {
    this.stopInactivityWatch();
    this.stopMicrosoftTokenWatch();
    this.isUserMenuOpen.set(false);
    this.activeNavItem.set(null);
    this.entraBranding.set(null);
    this.entraBrandingService.clear();
    this.authService.logout();
    void this.volverAlAccesoDelCliente();
    if (!this.msalService) {
      return;
    }

    // Se limpia solo el caché local de tokens. NO se usa logoutRedirect porque ése hace un
    // cierre de sesión global contra Entra y tumbaría también Outlook, Teams y cualquier otra
    // sesión de Microsoft del navegador. Al volver a entrar, MSAL consigue un token nuevo en
    // silencio y no se piden credenciales.
    void this.msalService.instance.clearCache({
      account: this.msalService.instance.getActiveAccount() ?? undefined
    });
  }

  private async loadAuthClientSettings(): Promise<void> {
    try {
      const settings = await firstValueFrom(
        this.http.get<AuthClientSettings>(`${environment.apiUrl}/auth/settings`)
      );
      this.inactivityLogoutMinutes = this.normalizeInactivityLogoutMinutes(settings.InactivityLogoutMinutes);
    } catch {
      this.inactivityLogoutMinutes = 15;
    }
  }

  private startInactivityWatch(): void {
    if (!this.activityListenersAttached) {
      this.activityEvents.forEach(eventName => {
        window.addEventListener(eventName, this.handleUserActivity, { passive: true });
      });
      this.activityListenersAttached = true;
    }

    this.scheduleInactivityLogout();
  }

  private stopInactivityWatch(): void {
    if (this.inactivityTimerId !== null) {
      window.clearTimeout(this.inactivityTimerId);
      this.inactivityTimerId = null;
    }

    if (this.sessionExpirationTimerId !== null) {
      window.clearTimeout(this.sessionExpirationTimerId);
      this.sessionExpirationTimerId = null;
    }

    if (!this.activityListenersAttached) {
      return;
    }

    this.activityEvents.forEach(eventName => {
      window.removeEventListener(eventName, this.handleUserActivity);
    });
    this.activityListenersAttached = false;
  }

  private scheduleInactivityLogout(): void {
    if (!this.isLoggedIn()) {
      return;
    }

    if (this.inactivityTimerId !== null) {
      window.clearTimeout(this.inactivityTimerId);
    }

    this.inactivityTimerId = window.setTimeout(() => {
      this.logout();
    }, this.inactivityLogoutMinutes * 60 * 1000);

    // La sesión es deslizante: mientras haya actividad se renueva también del lado del backend,
    // porque el ticket de licencias caduca con su propio reloj.
    void this.refrescarSesionSiHaceFalta();
  }

  /**
   * Renueva la sesión como mucho una vez por tercio del tiempo de inactividad, para no llamar al
   * backend con cada movimiento del ratón.
   */
  private async refrescarSesionSiHaceFalta(): Promise<void> {
    if (!this.isLoggedIn()) {
      return;
    }

    const ahora = Date.now();
    const intervaloMinimo = Math.max(60_000, (this.inactivityLogoutMinutes * 60 * 1000) / 3);
    if (ahora - this.lastSessionRefreshAt < intervaloMinimo) {
      return;
    }

    this.lastSessionRefreshAt = ahora;
    if (await this.authService.refreshSession()) {
      this.startSessionExpirationWatch();
    }
  }

  private startSessionExpirationWatch(): void {
    if (this.sessionExpirationTimerId !== null) {
      window.clearTimeout(this.sessionExpirationTimerId);
      this.sessionExpirationTimerId = null;
    }

    const expiresAt = this.authService.licenseSessionExpiresAt();
    if (!expiresAt) {
      return;
    }

    const remainingMs = expiresAt - Date.now();
    if (remainingMs <= 0) {
      this.logout();
      return;
    }

    this.sessionExpirationTimerId = window.setTimeout(() => {
      this.logout();
    }, remainingMs);
  }

  private normalizeInactivityLogoutMinutes(value: number | undefined): number {
    if (!Number.isFinite(value) || !value || value < 1) {
      return 15;
    }

    return Math.floor(value);
  }

  private async loadEntraBranding(): Promise<void> {
    try {
      // Solo la foto de perfil: el branding por tenant exige Organization.Read.All, que
      // requiere consentimiento de administrador y no está registrado en la app.
      const profilePhotoUrl = await this.entraBrandingService.loadProfilePhoto();
      this.authService.setProfilePhoto(profilePhotoUrl);
    } catch {
      this.entraBranding.set(null);
      this.authService.clearProfilePhoto();
    }
  }
}
